import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Clock, ArrowRight, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import EpisodeArtwork from "@/components/podcast/EpisodeArtwork";
import { CinemaCamera } from "@/components/podcast/StudioGear";

export default function LatestEpisodes() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .get("/episodes")
      .then((res) => {
        if (active) setEpisodes(res.data.slice(0, 5));
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const featured = episodes[0];
  const rest = episodes.slice(1);

  // Hide entire section when there are no episodes to show.
  if (!loading && episodes.length === 0) return null;

  return (
    <section
      data-testid="latest-episodes"
      className="py-20 md:py-32 bg-[#0A0A0A] film-grain relative overflow-hidden"
    >
      <CinemaCamera
        className="absolute -left-20 -bottom-10 w-[28rem] hidden lg:block"
        color="#1E6FE0"
        strokeWidth={1}
        style={{ opacity: 0.06 }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#1E6FE0] mb-3">
              The Feed
            </p>
            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none">
              Latest <span className="text-[#E10600]">Drops</span>
            </h2>
          </div>
          <Link
            to="/episodes"
            data-testid="latest-episodes-view-all"
            className="btn-secondary"
          >
            All Episodes
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm">Loading episodes…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Featured episode (large left) */}
            {featured && (
              <Link
                to={`/episodes#${featured.slug}`}
                data-testid="episode-card-0"
                className="lg:col-span-7 group border border-[#262626] hover:border-[#E10600] bg-[#0A0A0A] flex flex-col md:flex-row transition-colors"
              >
                <div className="w-full md:w-2/5 flex-shrink-0">
                  <EpisodeArtwork episode={featured} size="md" />
                </div>
                <div className="flex-1 p-6 md:p-8 flex flex-col">
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-[#E10600] text-white text-[10px] font-bold tracking-widest">
                      LATEST
                    </span>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">
                      {featured.number}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-3xl md:text-5xl uppercase leading-[0.95] text-white group-hover:text-[#E10600] transition-colors">
                    {featured.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400">With {featured.guest}</p>
                  <p className="mt-4 text-zinc-400 text-sm leading-relaxed line-clamp-3">
                    {featured.description}
                  </p>

                  <div className="mt-auto pt-6 flex items-center gap-5 text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {featured.published_at}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {featured.duration}
                    </span>
                  </div>

                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold tracking-widest uppercase text-[#E10600]">
                    <Play className="w-4 h-4" fill="currentColor" />
                    Play Episode
                  </div>
                </div>
              </Link>
            )}

            {/* Smaller list of recent episodes */}
            <div className="lg:col-span-5 flex flex-col gap-px bg-[#262626] border border-[#262626]">
              {rest.map((ep, idx) => (
                <Link
                  key={ep.slug}
                  to={`/episodes#${ep.slug}`}
                  data-testid={`episode-card-${idx + 1}`}
                  className="group bg-[#0A0A0A] hover:bg-[#121212] p-4 flex items-center gap-4 transition-colors"
                >
                  <div className="w-20 h-20 flex-shrink-0">
                    <EpisodeArtwork episode={ep} size="sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#E10600] font-mono">
                      {ep.number} · {ep.duration}
                    </p>
                    <h4 className="mt-1 font-display text-lg uppercase leading-tight text-white group-hover:text-[#E10600] transition-colors line-clamp-2">
                      {ep.title}
                    </h4>
                    <p className="mt-1 text-xs text-zinc-500 truncate">w/ {ep.guest}</p>
                  </div>
                  <Play className="w-5 h-5 text-zinc-600 group-hover:text-[#E10600] flex-shrink-0" fill="currentColor" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
